import type {
  GroupByContext,
  MessagePartStatus,
  PartState,
  ToolCallMessagePartStatus,
} from '@assistant-ui/react';
import { TextMessagePartProvider, groupPartByType, useAuiState } from '@assistant-ui/react';
import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown';
import { Brain, ChevronDown, CircleAlert, CircleCheck, Wrench } from 'lucide-react';
import * as React from 'react';
import remarkGfm from 'remark-gfm';
import { cn } from '../lib/utils.js';
import { formatSpecDuration } from './assistant-action-bar.js';
import {
  SMOOTH_COMMIT_MS,
  STREAM_FADE_IN,
  reasoningMarkdownComponents,
  streamingReasoningMarkdownComponents,
} from './assistant-markdown-text.js';
import { Button } from './button.js';

export type SpecProjectionEntity = 'projects' | 'accounts' | 'contacts';

/** Record ids a search returned (never fields); the web looks them up as the member. */
export interface SpecToolResultProjection {
  entity: SpecProjectionEntity;
  ids: string[];
  total: number | null;
  totalIsCapped: boolean;
  /** False when nothing is drawn; the search still replaces an earlier one's records. */
  shown: boolean;
}

export interface SpecToolStepResult {
  type: 'spec-tool-step';
  status: string;
  durationMs: number;
  resultBytes: number;
  /** Server-derived count, e.g. "3 records". Never a value from the result. */
  summary?: string;
  projection?: SpecToolResultProjection;
}

const PROJECTION_ENTITIES: readonly string[] = ['projects', 'accounts', 'contacts'];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function readToolResultProjection(value: unknown): SpecToolResultProjection | null {
  if (typeof value !== 'object' || value === null) return null;
  const { entity, ids, total, totalIsCapped } = value as Record<string, unknown>;
  // Saved before `shown` existed, when every projection was drawn.
  const shown = (value as Record<string, unknown>).shown ?? true;
  if (typeof entity !== 'string' || !PROJECTION_ENTITIES.includes(entity)) return null;
  if (typeof shown !== 'boolean') return null;
  if (!Array.isArray(ids) || ids.length > 25 || (shown && ids.length === 0)) return null;
  if (!ids.every((id) => typeof id === 'string' && UUID.test(id))) return null;
  if (total !== null && (typeof total !== 'number' || !Number.isFinite(total))) return null;
  if (typeof totalIsCapped !== 'boolean') return null;
  return {
    entity: entity as SpecProjectionEntity,
    ids: ids as string[],
    total,
    totalIsCapped,
    shown,
  };
}

// Saved before 2026-10-19, an enrichment step's summary carried its charge; a step never shows one.
const CHARGE_SEGMENT = /\s·\s(?:\d[\d.,]*\scredits|free)(?=\s·|$)/g;
const CHARGED_STEP_TOOLS = new Set([
  'search_people',
  'search_companies',
  'reveal_contact',
  'enrich_from_linkedin',
]);

export function asToolStep(result: unknown, toolName?: string): SpecToolStepResult | null {
  if (typeof result !== 'object' || result === null) return null;
  const r = result as Record<string, unknown>;
  if (r.type !== 'spec-tool-step' || typeof r.status !== 'string') return null;
  const projection = readToolResultProjection(r.projection);
  const raw = typeof r.summary === 'string' ? r.summary : '';
  const summary =
    toolName && CHARGED_STEP_TOOLS.has(toolName) ? raw.replace(CHARGE_SEGMENT, '') : raw;
  return {
    type: 'spec-tool-step',
    status: r.status,
    durationMs: typeof r.durationMs === 'number' ? r.durationMs : 0,
    resultBytes: typeof r.resultBytes === 'number' ? r.resultBytes : 0,
    ...(summary.length > 0 ? { summary } : {}),
    ...(projection ? { projection } : {}),
  };
}

/** A search's records, shown in the answer; the web adds this part beside the search's own step. */
export interface SpecToolRecordsResult {
  type: 'spec-tool-records';
  projection: SpecToolResultProjection;
}

export function asToolRecords(result: unknown): SpecToolRecordsResult | null {
  if (typeof result !== 'object' || result === null) return null;
  const r = result as Record<string, unknown>;
  if (r.type !== 'spec-tool-records') return null;
  const projection = readToolResultProjection(r.projection);
  return projection?.shown ? { type: 'spec-tool-records', projection } : null;
}

/**
 * True while a read is in flight and Spec has streamed nothing about it yet.
 *
 * assistant-ui normalizes an absent `args` to `{}`, never `undefined` (see
 * `fromThreadMessageLike`), so an empty object is the "no arguments known"
 * signal here, not literally `undefined`. Spec never streams live args or a
 * partial result for an ordinary read, so this is unambiguous: a tool call
 * with either already carries a shape (an action proposal, or `result` set)
 * that this is deliberately never true for.
 */
export function isPendingToolStep(
  status: { type: string },
  result: unknown,
  args: unknown,
): boolean {
  const hasNoArgs =
    args === undefined ||
    (typeof args === 'object' && args !== null && Object.keys(args).length === 0);
  return status.type === 'running' && result === undefined && hasNoArgs;
}

// Nothing left to act on. `unknown` and `blocked` stay out: both ask the member to go do something.
const FOLDED_PROPOSAL_STATES = new Set(['succeeded', 'failed', 'denied', 'expired']);
const PROPOSAL_RESULT_TYPES = new Set(['spec-action-proposal', 'spec-connection-proposal']);

/** True when a tool result is an approval proposal that has been decided and folds into the steps. */
export function isFoldedProposalResult(result: unknown): boolean {
  if (typeof result !== 'object' || result === null) return false;
  const r = result as Record<string, unknown>;
  if (r.unknownOutcome === true) return false;
  const proposal =
    typeof r.proposal === 'object' && r.proposal !== null
      ? (r.proposal as Record<string, unknown>)
      : r;
  return (
    typeof proposal.type === 'string' &&
    PROPOSAL_RESULT_TYPES.has(proposal.type) &&
    typeof proposal.state === 'string' &&
    FOLDED_PROPOSAL_STATES.has(proposal.state)
  );
}

/** True for a proposal still waiting on a decision or a confirmed outcome; it pins to the end of the turn. */
export function isAwaitingProposalResult(result: unknown): boolean {
  if (typeof result !== 'object' || result === null) return false;
  const r = result as Record<string, unknown>;
  const proposal =
    typeof r.proposal === 'object' && r.proposal !== null
      ? (r.proposal as Record<string, unknown>)
      : r;
  return (
    typeof proposal.type === 'string' &&
    PROPOSAL_RESULT_TYPES.has(proposal.type) &&
    !isFoldedProposalResult(result)
  );
}

// create_skill and update_skill are the retired names, kept so chats from before manage_skill still show their draft.
const ARTIFACT_STEP_TOOLS = new Set(['create_skill', 'update_skill']);
const ARTIFACT_COMMANDS = new Set(['create', 'update']);

/** An authoring write: a retired create/update tool, or manage_skill whose command (when recorded) is create or update. */
function isAuthoringWrite(toolName: string, args: unknown): boolean {
  if (ARTIFACT_STEP_TOOLS.has(toolName)) return true;
  if (toolName !== 'manage_skill') return false;
  const command =
    typeof args === 'object' && args !== null
      ? (args as Record<string, unknown>).command
      : undefined;
  return command === undefined || (typeof command === 'string' && ARTIFACT_COMMANDS.has(command));
}

/** Whole seconds for a step row; anything under a second reads `<1s`. */
export function formatStepDuration(ms: number): string {
  if (ms < 1000) return '<1s';
  return ms < 60_000 ? `${Math.round(ms / 1000)}s` : formatSpecDuration(ms);
}

/** True for a successful authoring write; the turn renders its draft as an artifact at the end. */
export function isArtifactStepResult(toolName: unknown, result: unknown, args?: unknown): boolean {
  return (
    typeof toolName === 'string' &&
    isAuthoringWrite(toolName, args) &&
    asToolStep(result)?.status === 'ok'
  );
}

const baseGroupBy = groupPartByType({
  reasoning: ['group-chainOfThought'],
  'tool-call': ['group-chainOfThought'],
});

/**
 * Reasoning always groups. A tool-call groups when it is an ordinary read:
 * either settled with a `spec-tool-step` result, or still running with
 * nothing streamed about it yet. A decided action proposal groups too; one
 * still awaiting a decision stays top-level so approval UI is never folded
 * away, and a tool call outside Spec's own vocabulary (real args, a non-step
 * result) renders exactly as it always has.
 */
/** True when a tool result is a question set the member answers in the composer. */
export function isQuestionSetResult(result: unknown): boolean {
  return (
    typeof result === 'object' &&
    result !== null &&
    (result as { type?: unknown }).type === 'spec-question'
  );
}

export const specChainOfThoughtGroupBy = (
  part: PartState,
  context?: GroupByContext,
): readonly 'group-chainOfThought'[] => {
  const typed = part as {
    type: string;
    status?: { type: string };
    result?: unknown;
    args?: unknown;
  };
  if (
    typed.type === 'tool-call' &&
    asToolStep(typed.result) === null &&
    !isFoldedProposalResult(typed.result) &&
    !isQuestionSetResult(typed.result) &&
    !(typed.status && isPendingToolStep(typed.status, typed.result, typed.args))
  ) {
    return [];
  }
  return baseGroupBy(part, context);
};

/**
 * Reasoning prose, muted, flattened markdown behind the same icon gutter the
 * tool steps use -- without it the prose and the step names sit on different
 * left edges. Wrapped in its own text-part provider because the markdown
 * primitive reads from one, and none is established for a reasoning-type leaf
 * rendered from `MessagePrimitive.GroupedParts`.
 */
export function ParcelReasoningPart({
  text,
  running = false,
}: { text: string; running?: boolean }) {
  return (
    <div data-slot="assistant-reasoning-row" className={cn(STEP_ROW, 'items-start')}>
      {/* A line box, not a nudge: 1.625em at text-sm is exactly the prose's
          line height, so the glyph centres on the first line at any zoom. */}
      <span className="flex h-[1.625em] items-center text-sm">
        <Brain aria-hidden className="size-3.5 shrink-0 text-muted-foreground/60" />
      </span>
      {/* The part's real status, not a constant: `useSmooth` snaps to the whole
          string and stops the animator for anything not marked running. */}
      <TextMessagePartProvider text={text} isRunning={running}>
        <MarkdownTextPrimitive
          containerProps={
            { 'data-slot': 'assistant-reasoning' } as React.HTMLAttributes<HTMLDivElement>
          }
          className={cn(
            'min-w-0 break-words text-sm leading-relaxed text-muted-foreground',
            STREAM_FADE_IN,
          )}
          smooth={{ minCommitMs: SMOOTH_COMMIT_MS }}
          remarkPlugins={[remarkGfm]}
          components={running ? streamingReasoningMarkdownComponents : reasoningMarkdownComponents}
        />
      </TextMessagePartProvider>
    </div>
  );
}

/**
 * One gutter column, shared by reasoning and steps, so text shares a left edge.
 * Grid, not flex: `minmax(0,1fr)` cannot be driven past the column by an
 * unbreakable tool name the way a flex item's min-content floor can.
 */
const STEP_ROW = 'grid grid-cols-[0.875rem_minmax(0,1fr)] gap-2 text-sm';

type StepMark = { label: string; tone: string; Icon: typeof Wrench };

const STEP_STATUS: Record<string, StepMark> = {
  ok: { label: 'Succeeded', tone: 'text-brand', Icon: CircleCheck },
  summarized: { label: 'Succeeded, summarized', tone: 'text-brand', Icon: CircleCheck },
  error: { label: 'Failed', tone: 'text-destructive', Icon: CircleAlert },
  unknown_tool: { label: 'Unknown tool', tone: 'text-destructive', Icon: CircleAlert },
};

const RUNNING_MARK: StepMark = {
  label: 'Running',
  tone: 'text-muted-foreground motion-safe:animate-pulse',
  Icon: Wrench,
};

/** The first line of reasoning as plain words, for the header while Spec thinks; null when there are none yet. */
export function reasoningHeadline(text: string): string | null {
  const line = text
    .split('\n')
    .map((entry) => entry.replace(/[*_`#>]+/g, '').trim())
    .find((entry) => entry.length > 0);
  if (!line) return null;
  const end = line.search(/[.!?:](\s|$)/);
  const sentence = (end >= 0 ? line.slice(0, end) : line).trim();
  // The first clause reads as a title; the rest of the thought is in the panel.
  const clause =
    sentence
      .split(/[,;]\s|\s[-–—]\s|\s(?:so|then|and then|because|but|and I(?:['’](?:m|ll))?)\s/)[0]
      ?.trim() ?? '';
  const words = clause.split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;
  let headline = '';
  for (const word of words) {
    const next = headline ? `${headline} ${word}` : word;
    if (next.length > HEADLINE_CHARS) return `${headline || word.slice(0, HEADLINE_CHARS)}…`;
    headline = next;
  }
  return headline;
}

/** Longest header headline, so it reads as a title and leaves room for the clock and steps. */
const HEADLINE_CHARS = 48;

/** The steps panel opened before any step exists: one working row, replaced by the first real one. */
export function ParcelChainOfThoughtPlaceholder() {
  return (
    <div data-slot="assistant-chain-of-thought">
      <div aria-busy className="space-y-3 border-l border-border pl-4">
        <div data-slot="assistant-reasoning-row" className={cn(STEP_ROW, 'items-center')}>
          <Brain aria-hidden className="size-3.5 shrink-0 text-muted-foreground/60" />
          <span className="text-muted-foreground text-shimmer">Thinking…</span>
        </div>
      </div>
    </div>
  );
}

const PENDING_MARK: StepMark = { label: 'Finished', tone: 'text-muted-foreground', Icon: Wrench };

export function ParcelToolStepRow({
  toolName,
  label,
  icon,
  step,
  running,
  detail,
}: {
  toolName: string;
  /** What to show instead of the wire name, when the app can name the tool. */
  label?: string;
  /** The source's own mark, for a tool that came from somewhere recognizable. */
  icon?: React.ReactNode;
  step: SpecToolStepResult | null;
  running: boolean;
  /** What the call sent and received; given `open` so the app can fetch only once asked. */
  detail?: (open: boolean) => React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  // A settled step can be opened; a running one has nothing to show yet.
  const expandable = detail !== undefined && step !== null && !running;
  const mark = running ? RUNNING_MARK : (STEP_STATUS[step?.status ?? ''] ?? PENDING_MARK);
  const StatusIcon = mark.Icon;
  // A source mark yields the gutter to a failure: which connector ran matters
  // less than that it did not, and there is only one column to say it in.
  const showIcon = icon != null && mark.tone !== 'text-destructive';
  const cells = (
    <>
      {/* The outcome names the gutter cell itself. A visually-hidden span would
          be `position:absolute` with no positioned ancestor, so it escapes the
          transcript's scroller and sizes the document instead. */}
      <span role="img" aria-label={mark.label} className="flex size-3.5 shrink-0 items-center">
        {showIcon ? (
          // No status tone here: a brand mark is not a status, and tinting a logo
          // with it misreports both.
          <span className="flex size-3.5 items-center justify-center text-foreground">{icon}</span>
        ) : (
          <StatusIcon aria-hidden className={cn('size-3.5 shrink-0', mark.tone)} />
        )}
      </span>
      <span className="flex min-w-0 items-center gap-2">
        {label ? (
          <span
            className={cn(
              'truncate',
              running ? 'text-muted-foreground text-shimmer' : 'text-foreground',
            )}
          >
            {label}
          </span>
        ) : (
          <span
            className={cn(
              'truncate font-mono text-[0.85em]',
              running ? 'text-muted-foreground text-shimmer' : 'text-foreground',
            )}
          >
            {toolName}
          </span>
        )}
        {step?.summary ? (
          <span className="truncate text-muted-foreground text-xs">{step.summary}</span>
        ) : null}
        {step ? (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {formatStepDuration(step.durationMs)}
          </span>
        ) : null}
        {expandable ? (
          <ChevronDown
            aria-hidden
            className={cn(
              'size-3 shrink-0 text-muted-foreground transition-transform',
              !open && '-rotate-90',
            )}
          />
        ) : null}
      </span>
    </>
  );
  const status = running ? 'running' : (step?.status ?? 'pending');
  if (!expandable) {
    return (
      <div
        data-slot="assistant-tool-step"
        data-status={status}
        className={cn(STEP_ROW, 'items-center py-1')}
      >
        {cells}
      </div>
    );
  }
  return (
    <div data-slot="assistant-tool-step" data-status={status}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(STEP_ROW, 'w-full items-center py-1 text-left')}
      >
        {cells}
      </button>
      {open ? <div className="mt-1 mb-2">{detail(open)}</div> : null}
    </div>
  );
}

/** One expandable row inside the steps: an icon in the step gutter, a label, and detail on demand. */
export function ParcelStepDisclosureRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <div data-slot="assistant-step-disclosure">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(STEP_ROW, 'w-full items-center py-1 text-left')}
      >
        <span
          aria-hidden
          className="flex size-3.5 shrink-0 items-center justify-center text-muted-foreground"
        >
          {icon}
        </span>
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-foreground">{label}</span>
          <ChevronDown
            aria-hidden
            className={cn(
              'size-3 shrink-0 text-muted-foreground transition-transform',
              !open && '-rotate-90',
            )}
          />
        </span>
      </button>
      {open ? (
        <div className={cn(STEP_ROW, 'mt-1 mb-2')}>
          <span aria-hidden />
          <div className="min-w-0">{children}</div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A decided approval inside the steps: one row naming the action and its
 * outcome, expanding to the full card for anyone who wants the record.
 */
export function ParcelProposalStepRow({
  label,
  icon,
  statusLabel,
  failed,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  statusLabel: string;
  failed: boolean;
  /** The full proposal card, shown when the row is expanded. */
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  const Mark = failed ? CircleAlert : CircleCheck;
  const showIcon = icon != null && !failed;
  return (
    <div data-slot="assistant-proposal-step">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(STEP_ROW, 'w-full items-center py-1 text-left')}
      >
        {/* Hidden: the status is already visible text in the button's name. */}
        <span aria-hidden className="flex size-3.5 shrink-0 items-center">
          {showIcon ? (
            <span className="flex size-3.5 items-center justify-center text-foreground">
              {icon}
            </span>
          ) : (
            <Mark
              aria-hidden
              className={cn(
                'size-3.5 shrink-0',
                failed ? 'text-destructive' : 'text-muted-foreground',
              )}
            />
          )}
        </span>
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-foreground">{label}</span>
          <span
            className={cn(
              'shrink-0 text-xs',
              failed ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {statusLabel}
          </span>
          <ChevronDown
            aria-hidden
            className={cn(
              'size-3 shrink-0 text-muted-foreground transition-transform',
              !open && '-rotate-90',
            )}
          />
        </span>
      </button>
      {open ? <div className="mt-1">{children}</div> : null}
    </div>
  );
}

/** The `group-chainOfThought` node `MessagePrimitive.GroupedParts` renders for reasoning + tool-step parts. */
export interface ChainOfThoughtGroupPart {
  type: 'group-chainOfThought';
  status: MessagePartStatus | ToolCallMessagePartStatus;
  indices: readonly number[];
}

/**
 * Open state for a message's chain of thought, owned by the message header so
 * the chevron sits beside the Spec mark rather than on a row of its own.
 */
export interface ChainOfThoughtDisclosure {
  open: boolean;
  toggle: () => void;
}

export const ChainOfThoughtContext = React.createContext<ChainOfThoughtDisclosure | null>(null);

/** True for any part the chain of thought would fold in. */
export function isChainOfThoughtPart(part: PartState): boolean {
  return specChainOfThoughtGroupBy(part).length > 0;
}

/**
 * Collapsed until the member opens it, including while a turn is streaming.
 * Forced closed when the turn lands so the answer is the focus; after that it
 * stays under their control. Shared by the header and any group that lacks one.
 */
export function useChainOfThoughtDisclosure(running: boolean): ChainOfThoughtDisclosure {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    if (!running) setOpen(false);
  }, [running]);
  const toggle = React.useCallback(() => setOpen((value) => !value), []);
  return React.useMemo(() => ({ open, toggle }), [open, toggle]);
}

/**
 * The header's disclosure control: chevron plus a quiet step summary. Named
 * for assistive tech as "Thinking" while running, "Thought for Ns · N steps"
 * after.
 */
export function ParcelChainOfThoughtToggle({
  disclosure,
  running,
  children,
  className,
}: {
  disclosure: ChainOfThoughtDisclosure;
  running: boolean;
  /** Rendered inside the button, ahead of the chevron, so it is part of the target. */
  children?: React.ReactNode;
  className?: string;
}) {
  const stepCount = useAuiState(
    (s) =>
      s.message.parts.filter((part) => part.type === 'tool-call' && isChainOfThoughtPart(part))
        .length,
  );
  const durationMs = useAuiState((s) => s.message.metadata?.timing?.firstTokenTime);
  const summary = stepCount > 0 ? `${stepCount} ${stepCount === 1 ? 'step' : 'steps'}` : '';
  const label = running
    ? 'Thinking'
    : `Thought${durationMs ? ` for ${formatSpecDuration(durationMs)}` : ''}${
        summary ? ` · ${summary}` : ''
      }`;
  return (
    // The step count sits BESIDE the button, not inside it: it is a readout,
    // and hovering the whole line to read it makes it look like a second target.
    <span className={cn('inline-flex min-w-0 max-w-full items-center gap-1.5', className)}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={label}
        aria-expanded={disclosure.open}
        onClick={disclosure.toggle}
        className={cn(
          // A header line rather than a form control: compact, normal weight,
          // and icons at their own size instead of the base 16px.
          '-ml-2 h-auto min-w-0 max-w-full gap-1.5 px-2 py-1 font-normal text-xs [&_svg]:size-3.5',
        )}
      >
        {children}
        <ChevronDown
          aria-hidden
          className={cn('transition-transform', !disclosure.open && '-rotate-90')}
        />
      </Button>
      {summary ? <span className="shrink-0 whitespace-nowrap text-xs">{summary}</span> : null}
    </span>
  );
}

/** Body of the chain of thought; the header owns its toggle when one is mounted. */
export function ParcelChainOfThoughtGroup({
  part,
  children,
}: {
  part: ChainOfThoughtGroupPart;
  children: React.ReactNode;
}) {
  const running = part.status.type === 'running';
  const own = useChainOfThoughtDisclosure(running);
  const shared = React.useContext(ChainOfThoughtContext);
  const disclosure = shared ?? own;
  return (
    <div data-slot="assistant-chain-of-thought">
      {shared ? null : <ParcelChainOfThoughtToggle disclosure={disclosure} running={running} />}
      {disclosure.open ? (
        <div aria-busy={running} className="space-y-3 border-l border-border pl-4">
          {children}
        </div>
      ) : null}
    </div>
  );
}
